import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useHistory } from 'react-router-dom';
import API_BASE_URL, { getImageUrl } from '../config';
import './GuestList.css';

interface Guest {
    _id: string;
    name: string;
    phone: string;
    familySide: string;
    relationship?: string;
    profilePhoto?: string;
    status: 'pending' | 'approved' | 'rejected';
    createdAt: string;
}

const GuestList: React.FC = () => {
    const { accessToken, logout, loading: authLoading } = useAuth();
    const history = useHistory();
    const [guests, setGuests] = useState<Guest[]>([]);
    const [familySide, setFamilySide] = useState<'groom' | 'bride' | ''>('');
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<'pending' | 'approved' | 'rejected'>('pending');

    // Approval Modal State
    const [showApproveModal, setShowApproveModal] = useState(false);
    const [selectedGuest, setSelectedGuest] = useState<Guest | null>(null);
    const [roomNumber, setRoomNumber] = useState('');
    const [dietaryInfo, setDietaryInfo] = useState('');
    const [location, setLocation] = useState('');
    const [giftCode, setGiftCode] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Import/Broadcast State
    const [showImportModal, setShowImportModal] = useState(false);
    const [showBroadcastModal, setShowBroadcastModal] = useState(false);
    const [broadcastMessage, setBroadcastMessage] = useState('');
    const [importFile, setImportFile] = useState<File | null>(null);

    // Add Guest State
    const [showAddModal, setShowAddModal] = useState(false);
    const [newGuestName, setNewGuestName] = useState('');
    const [newGuestPhone, setNewGuestPhone] = useState('');
    const [newGuestRelationship, setNewGuestRelationship] = useState('');
    const [newGuestSide, setNewGuestSide] = useState<'groom' | 'bride' | ''>('');

    const fetchData = async () => {
        try {
            // Fetch Profile
            const profileRes = await fetch(`${API_BASE_URL}/api/admin/profile`, {
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            const profileData = await profileRes.json();
            if (profileRes.ok) {
                setFamilySide(profileData.admin.familySide);
            }

            // Fetch All Guests
            const guestsRes = await fetch(`${API_BASE_URL}/api/guests`, {
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            const guestsData = await guestsRes.json();
            if (guestsRes.ok) {
                setGuests(guestsData.guests);
            } else if (guestsRes.status === 401) {
                logout();
                history.push('/login');
            }
        } catch (err) {
            console.error('Error fetching guests:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!authLoading) {
            if (accessToken) {
                fetchData();
            } else {
                history.push('/login');
            }
        }
    }, [accessToken, authLoading, history]);

    const handleOpenApproveModal = (guest: Guest) => {
        setSelectedGuest(guest);
        // Reset form or pre-fill if editing approved guest
        setRoomNumber('');
        setDietaryInfo('');
        setLocation('');
        setGiftCode(Math.floor(1000 + Math.random() * 9000).toString()); // Random 4-digit gift code
        setShowApproveModal(true);
    };

    const handleConfirmApproval = async () => {
        if (!selectedGuest) return;
        setIsSubmitting(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/guests/${selectedGuest._id}/status`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify({
                    status: 'approved',
                    roomNumber,
                    dietaryInfo,
                    location,
                    giftCode
                }),
            });

            if (response.ok) {
                setShowApproveModal(false);
                fetchData();
            } else {
                alert('Failed to approve guest');
            }
        } catch (err) {
            alert('Error during approval');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleImportExcel = async () => {
        if (!importFile) return;
        setIsSubmitting(true);
        try {
            const formData = new FormData();
            formData.append('excelFile', importFile);

            const response = await fetch(`${API_BASE_URL}/api/guests/import`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
                body: formData,
            });

            const data = await response.json();
            if (response.ok) {
                alert(data.message);
                setShowImportModal(false);
                setImportFile(null);
                setActiveTab('approved'); // Switch to approved tab to show new guests
                fetchData();
            } else {
                alert(data.message || 'Import failed');
            }
        } catch (err) {
            alert('Error importing guests');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSendBroadcast = async () => {
        if (!broadcastMessage) return;
        setIsSubmitting(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/guests/broadcast`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify({ message: broadcastMessage }),
            });

            const data = await response.json();
            if (response.ok) {
                alert(data.message);
                setShowBroadcastModal(false);
                setBroadcastMessage('');
            } else {
                alert(data.message || 'Broadcast failed');
            }
        } catch (err) {
            alert('Error sending broadcast');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleAddGuest = async () => {
        if (!newGuestName || !newGuestPhone) return;
        setIsSubmitting(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/guests`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify({
                    name: newGuestName,
                    phone: newGuestPhone,
                    relationship: newGuestRelationship,
                    familySide: newGuestSide || familySide,
                }),
            });

            const data = await response.json();
            if (response.ok) {
                setShowAddModal(false);
                setNewGuestName('');
                setNewGuestPhone('');
                setNewGuestRelationship('');
                setNewGuestSide('');
                setActiveTab('approved');
                fetchData();
            } else {
                alert(data.message || 'Failed to add guest');
            }
        } catch (err) {
            alert('Error adding guest');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleUpdateStatus = async (id: string, status: 'approved' | 'rejected') => {
        if (status === 'approved') {
            const guest = guests.find(g => g._id === id);
            if (guest) handleOpenApproveModal(guest);
            return;
        }

        try {
            const response = await fetch(`${API_BASE_URL}/api/guests/${id}/status`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify({ status }),
            });

            if (response.ok) {
                fetchData();
            } else {
                alert('Failed to update status');
            }
        } catch (err) {
            alert('Error updating status');
        }
    };

    const [searchQuery, setSearchQuery] = useState('');
    const [sortField, setSortField] = useState<keyof Guest>('name');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

    const handleSort = (field: keyof Guest) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const sortedAndFilteredGuests = guests
        .filter(g => g.status === activeTab)
        .filter(g =>
            g.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            g.phone.includes(searchQuery) ||
            (g.relationship || '').toLowerCase().includes(searchQuery.toLowerCase())
        )
        .sort((a, b) => {
            const fieldA = a[sortField]?.toString().toLowerCase() || '';
            const fieldB = b[sortField]?.toString().toLowerCase() || '';

            if (fieldA < fieldB) return sortDirection === 'asc' ? -1 : 1;
            if (fieldA > fieldB) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });

    if (loading) return <div className="guest-list-container">Loading...</div>;

    return (
        <div className={`admin-dashboard guest-list-container ${familySide ? `theme-${familySide}` : ''}`}>
            <div className="guest-list-header">
                <div className="title-area">
                    <h1>Guest Directory</h1>
                    <p>Manage your wedding circle and access requests</p>
                    <div className="header-actions">
                        <button className="add-guest-btn" onClick={() => setShowAddModal(true)}>
                            <span>➕</span> Add Guest
                        </button>
                        <button className="import-btn" onClick={() => setShowImportModal(true)}>
                            <span>📥</span> Import Excel
                        </button>
                        <button className="broadcast-btn" onClick={() => setShowBroadcastModal(true)}>
                            <span>💬</span> Broadcast WhatsApp
                        </button>
                    </div>
                </div>
                <div className="search-area">
                    <div className="search-bar">
                        <span className="search-icon">🔍</span>
                        <input
                            type="text"
                            placeholder="Search names, phone, relationship..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        {searchQuery && (
                            <button className="clear-search" onClick={() => setSearchQuery('')}>&times;</button>
                        )}
                    </div>
                </div>
            </div>

            <div className="guest-tabs">
                <button
                    className={`tab-btn ${activeTab === 'pending' ? 'active' : ''}`}
                    onClick={() => setActiveTab('pending')}
                >
                    Requests ({guests.filter(g => g.status === 'pending').length})
                </button>
                <button
                    className={`tab-btn ${activeTab === 'approved' ? 'active' : ''}`}
                    onClick={() => setActiveTab('approved')}
                >
                    Approved ({guests.filter(g => g.status === 'approved').length})
                </button>
                <button
                    className={`tab-btn ${activeTab === 'rejected' ? 'active' : ''}`}
                    onClick={() => setActiveTab('rejected')}
                >
                    Rejected ({guests.filter(g => g.status === 'rejected').length})
                </button>
            </div>

            <div className="guest-list-content">
                {sortedAndFilteredGuests.length === 0 ? (
                    <div className="no-requests">
                        <div className="empty-icon">
                            {activeTab === 'pending' ? '🤝' : activeTab === 'approved' ? '🌟' : '🚫'}
                        </div>
                        <h2>No {activeTab} guests found</h2>
                        <p>{searchQuery ? 'Try adjusting your search query.' : 'Currently there are no guests in this category.'}</p>
                        {activeTab === 'approved' && !searchQuery && (
                            <button className="add-guest-btn" style={{ marginTop: '16px' }} onClick={() => setShowAddModal(true)}>
                                ➕ Add Your First Guest
                            </button>
                        )}
                    </div>
                ) : (
                    <>
                        {/* Mobile card view */}
                        <div className="guest-cards">
                            {sortedAndFilteredGuests.map((guest) => (
                                <div className="guest-card" key={guest._id}>
                                    <div className="guest-card-header">
                                        <div className="guest-avatar">
                                            {guest.profilePhoto ? (
                                                <img src={getImageUrl(guest.profilePhoto)} alt={guest.name} />
                                            ) : (
                                                <div className="avatar-placeholder">{guest.name.charAt(0).toUpperCase()}</div>
                                            )}
                                        </div>
                                        <div className="guest-info">
                                            <div className="guest-name">{guest.name}</div>
                                            <div className="guest-relationship">{guest.relationship || 'Guest'} · {guest.phone}</div>
                                        </div>
                                    </div>
                                    <div className="guest-card-details">
                                        <span className={`side-badge ${guest.familySide}`}>{guest.familySide}</span>
                                        <span className={`status-badge status-${guest.status}`}>{guest.status}</span>
                                    </div>
                                    <div className="guest-card-actions">
                                        {guest.status === 'pending' && (
                                            <>
                                                <button className="icon-btn approve" onClick={() => handleUpdateStatus(guest._id, 'approved')} title="Approve">✅</button>
                                                <button className="icon-btn reject" onClick={() => handleUpdateStatus(guest._id, 'rejected')} title="Reject">❌</button>
                                            </>
                                        )}
                                        {guest.status === 'approved' && (
                                            <button className="icon-btn revoke" onClick={() => handleUpdateStatus(guest._id, 'rejected')} title="Revoke Access">🚫</button>
                                        )}
                                        {guest.status === 'rejected' && (
                                            <button className="icon-btn approve" onClick={() => handleUpdateStatus(guest._id, 'approved')} title="Re-Approve">✅</button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Desktop table view */}
                        <div className="table-responsive">
                            <table className="guest-table">
                                <thead>
                                    <tr>
                                        <th>Photo</th>
                                        <th onClick={() => handleSort('name')} className="sortable">
                                            Name {sortField === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
                                        </th>
                                        <th onClick={() => handleSort('phone')} className="sortable">
                                            Phone {sortField === 'phone' && (sortDirection === 'asc' ? '↑' : '↓')}
                                        </th>
                                        <th onClick={() => handleSort('relationship')} className="sortable">
                                            Relationship {sortField === 'relationship' && (sortDirection === 'asc' ? '↑' : '↓')}
                                        </th>
                                        <th onClick={() => handleSort('familySide')} className="sortable">
                                            Side {sortField === 'familySide' && (sortDirection === 'asc' ? '↑' : '↓')}
                                        </th>
                                        <th onClick={() => handleSort('createdAt')} className="sortable">
                                            Joined {sortField === 'createdAt' && (sortDirection === 'asc' ? '↑' : '↓')}
                                        </th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedAndFilteredGuests.map((guest) => (
                                        <tr key={guest._id}>
                                            <td>
                                                <div className="table-avatar">
                                                    {guest.profilePhoto ? (
                                                        <img src={getImageUrl(guest.profilePhoto)} alt={guest.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                    ) : (
                                                        <div className="avatar-placeholder">{guest.name.charAt(0).toUpperCase()}</div>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="guest-name-cell">{guest.name}</td>
                                            <td>{guest.phone}</td>
                                            <td>
                                                <span className="relationship-tag">{guest.relationship || 'Guest'}</span>
                                            </td>
                                            <td className="side-cell">
                                                <span className={`side-badge ${guest.familySide}`}>
                                                    {guest.familySide}
                                                </span>
                                            </td>
                                            <td>{new Date(guest.createdAt).toLocaleDateString()}</td>
                                            <td>
                                                <div className="table-actions">
                                                    {guest.status === 'pending' && (
                                                        <>
                                                            <button className="icon-btn approve" onClick={() => handleUpdateStatus(guest._id, 'approved')} title="Approve">✅</button>
                                                            <button className="icon-btn reject" onClick={() => handleUpdateStatus(guest._id, 'rejected')} title="Reject">❌</button>
                                                        </>
                                                    )}
                                                    {guest.status === 'approved' && (
                                                        <button className="icon-btn revoke" onClick={() => handleUpdateStatus(guest._id, 'rejected')} title="Revoke Access">🚫</button>
                                                    )}
                                                    {guest.status === 'rejected' && (
                                                        <button className="icon-btn approve" onClick={() => handleUpdateStatus(guest._id, 'approved')} title="Re-Approve">✅</button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>

            {/* Add Guest Modal */}
            {showAddModal && (
                <div className="modal-overlay">
                    <div className="approve-modal glass-panel">
                        <h2>Add New Guest ✨</h2>
                        <p>Manually add a guest — they'll be auto-approved</p>

                        <div className="modal-form">
                            <div className="form-group">
                                <label>Full Name *</label>
                                <input
                                    type="text"
                                    value={newGuestName}
                                    onChange={(e) => setNewGuestName(e.target.value)}
                                    placeholder="e.g. Priya Sharma"
                                    autoFocus
                                />
                            </div>

                            <div className="form-group">
                                <label>Mobile Number * (10 digits)</label>
                                <input
                                    type="tel"
                                    value={newGuestPhone}
                                    onChange={(e) => setNewGuestPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                                    placeholder="e.g. 9876543210"
                                    maxLength={10}
                                />
                            </div>

                            <div className="form-group">
                                <label>Relationship</label>
                                <input
                                    type="text"
                                    value={newGuestRelationship}
                                    onChange={(e) => setNewGuestRelationship(e.target.value)}
                                    placeholder="e.g. Uncle, Cousin, Friend"
                                />
                            </div>

                            <div className="form-group">
                                <label>Family Side</label>
                                <div className="side-selector">
                                    <button
                                        type="button"
                                        className={`side-option ${(newGuestSide || familySide) === 'groom' ? 'selected groom' : ''}`}
                                        onClick={() => setNewGuestSide('groom')}
                                    >
                                        🤵 Groom's Side
                                    </button>
                                    <button
                                        type="button"
                                        className={`side-option ${(newGuestSide || familySide) === 'bride' ? 'selected bride' : ''}`}
                                        onClick={() => setNewGuestSide('bride')}
                                    >
                                        👰 Bride's Side
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="modal-actions">
                            <button
                                className="cancel-btn"
                                onClick={() => {
                                    setShowAddModal(false);
                                    setNewGuestName('');
                                    setNewGuestPhone('');
                                    setNewGuestRelationship('');
                                    setNewGuestSide('');
                                }}
                                disabled={isSubmitting}
                            >
                                Cancel
                            </button>
                            <button
                                className="confirm-approve-btn"
                                onClick={handleAddGuest}
                                disabled={!newGuestName || newGuestPhone.length !== 10 || isSubmitting}
                            >
                                {isSubmitting ? 'Adding...' : 'Add Guest ✨'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Approval Modal */}
            {showApproveModal && (
                <div className="modal-overlay">
                    <div className="approve-modal glass-panel">
                        <h2>Approve {selectedGuest?.name}</h2>
                        <p>Complete the following details to finalize approval</p>

                        <div className="modal-form">
                            <div className="form-group">
                                <label>Allotted Room Number</label>
                                <input
                                    type="text"
                                    value={roomNumber}
                                    onChange={(e) => setRoomNumber(e.target.value)}
                                    placeholder="e.g. Room 402"
                                />
                            </div>

                            <div className="form-group">
                                <label>Dietary Information / Restrictions</label>
                                <input
                                    type="text"
                                    value={dietaryInfo}
                                    onChange={(e) => setDietaryInfo(e.target.value)}
                                    placeholder="e.g. Vegan, Nut Allergy"
                                />
                            </div>

                            <div className="form-group">
                                <label>Allotted Location</label>
                                <input
                                    type="text"
                                    value={location}
                                    onChange={(e) => setLocation(e.target.value)}
                                    placeholder="e.g. Main Hall, Garden Area"
                                />
                            </div>

                            <div className="form-group">
                                <label>Gift Allotment Code</label>
                                <div className="gift-code-input">
                                    <input
                                        type="text"
                                        value={giftCode}
                                        onChange={(e) => setGiftCode(e.target.value)}
                                        placeholder="4-digit code"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setGiftCode(Math.floor(1000 + Math.random() * 9000).toString())}
                                        className="regen-btn"
                                    >
                                        🔄
                                    </button>
                                </div>
                                <small>Auto-generated or enter manually</small>
                            </div>
                        </div>

                        <div className="modal-actions">
                            <button
                                className="cancel-btn"
                                onClick={() => setShowApproveModal(false)}
                                disabled={isSubmitting}
                            >
                                Cancel
                            </button>
                            <button
                                className="confirm-approve-btn"
                                onClick={handleConfirmApproval}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? 'Approving...' : 'Confirm Approval ✨'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Import Modal */}
            {showImportModal && (
                <div className="modal-overlay">
                    <div className="approve-modal glass-panel">
                        <h2>Import from Excel</h2>
                        <p>Bulk upload guests using a .xlsx file</p>

                        <div className="modal-form">
                            <div className="form-group">
                                <label>Select Excel File</label>
                                <input
                                    type="file"
                                    accept=".xlsx, .xls"
                                    onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                                />
                                <small>File must have 'Name' and 'Phone' columns</small>
                            </div>
                        </div>

                        <div className="modal-actions">
                            <button className="cancel-btn" onClick={() => setShowImportModal(false)}>Cancel</button>
                            <button
                                className="confirm-approve-btn"
                                onClick={handleImportExcel}
                                disabled={!importFile || isSubmitting}
                            >
                                {isSubmitting ? 'Importing...' : 'Start Import 📥'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Broadcast Modal */}
            {showBroadcastModal && (
                <div className="modal-overlay">
                    <div className="approve-modal broadcast-modal glass-panel">
                        <h2>WhatsApp Broadcast</h2>
                        <p>Send a message to all your approved guests</p>

                        <div className="modal-form">
                            <div className="form-group">
                                <label>Message Content</label>
                                <textarea
                                    value={broadcastMessage}
                                    onChange={(e) => setBroadcastMessage(e.target.value)}
                                    placeholder="Type your WhatsApp broadcast message here..."
                                    maxLength={1000}
                                />
                                <span className="character-count">{broadcastMessage.length}/1000</span>
                            </div>
                            <p className="broadcast-info">
                                Note: This will be sent as a direct WhatsApp message from your Meta Business number.
                            </p>
                        </div>

                        <div className="modal-actions">
                            <button className="cancel-btn" onClick={() => setShowBroadcastModal(false)}>Cancel</button>
                            <button
                                className="confirm-approve-btn"
                                onClick={handleSendBroadcast}
                                disabled={!broadcastMessage || isSubmitting}
                            >
                                {isSubmitting ? 'Sending...' : 'Send Broadcast 🚀'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GuestList;

